/**
 * `note` 분류 (DESIGN §15.6).
 *
 * DESIGN 은 이 위젯을 "공사·철거·이전 키워드 마이닝"으로 상정했지만 실측은
 * 달랐다 — 실제 note 는 **이용 제한 사유**다. 그래서 이 위젯의 역할은
 * `limitYn='Y'` 55% 가 무엇인지 설명하는 것이다: "입주민 전용"은 고장이 아니라
 * 접근 정책이므로, `user_facing` 모드의 가동률 18% 를 고장률로 읽으면 안 된다.
 */

import { useQuery } from '@tanstack/react-query'

import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import type { Envelope, NoteCategory } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './NoteCategories.css'

interface Data {
  categories: NoteCategory[]
  n_notes_matched: number
  unclassified_share: number | null
}

export function NoteCategories() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['quality/note_categories', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<Data>('/quality/note_categories', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<Data> | undefined
  const cats = env?.data.categories ?? []
  const max = Math.max(1, ...cats.map((c) => c.n_chargers))
  const unclassified = env?.data.unclassified_share ?? null
  const top = cats.find((c) => c.category !== '미분류')

  return (
    <ChartFrame
      title="note 분류 — 이용 제한 사유"
      question="limitYn='Y' 55%는 대체 무엇인가"
      meta={env?.meta}
      hideCoverage
      headline={
        top ? (
          /* 이상 신호가 아니라 사실이므로 잉크색 — 상태색은 경고일 때만 (§8.6 이탈 1) */
          <Headline value={top.n_chargers.toLocaleString('ko-KR')} unit={`기 · ${top.category}`} />
        ) : undefined
      }
      reading="충전기 설명(note) 문구를 키워드로 분류해 어느 사유가 몇 기에 붙어 있는지 셌습니다. 실측에서 절반이 넘는 충전기가 '이용 제한' 상태인데, 그 제한이 실제로 무엇인지를 여기서 확인합니다."
      encoding={[
        {
          role: '가로 막대',
          what: '해당 분류에 걸린 충전기 수',
          detail: '가장 많은 분류를 100% 로 두고 비례합니다. 오른쪽 첫 숫자가 실제 개수입니다.',
        },
        {
          role: '세로축',
          what: '분류 · 개수 내림차순',
          detail: '키워드는 config/codes/note_keywords.csv 에서 읽습니다 (코드에 하드코딩하지 않습니다).',
        },
        {
          role: '오른쪽 %',
          what: "그 분류 안에서 limitYn='Y' 인 비율",
          detail:
            '높을수록 그 문구가 이용 제한을 실제로 설명합니다. 낮으면 문구는 있으나 제한과 무관하다는 뜻입니다.',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={cats.length === 0}
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: 'note 는 철거 신호가 아니라 이용 제한 신호다.',
          text: 'DESIGN 은 "공사·철거·이전" 마이닝을 상정했으나 실측에서 그 표현은 29건뿐이었습니다 (§15.6). 실제로는 외부인 출입 제한·입주민 전용 같은 접근 제한이 대부분입니다.',
        },
        {
          label: '합계는 중복을 포함한다.',
          text: '한 note 가 여러 분류에 걸릴 수 있어 분류별 개수를 더하면 전체보다 커집니다.',
        },
        unclassified != null
          ? {
              label: `미분류 ${(unclassified * 100).toFixed(1)}%.`,
              text: '이 비중이 커지면 키워드 목록이 낡았다는 신호입니다 — config/codes/note_keywords.csv 를 갱신해야 합니다.',
            }
          : '',
      ]}
    >
      <ul className="notecat">
        {cats.map((c) => (
          <li key={c.category} className="notecat__row">
            <span className="notecat__name">{c.category}</span>
            <span className="notecat__bar">
              <span
                className="notecat__fill"
                style={{ width: `${(c.n_chargers / max) * 100}%` }}
              />
            </span>
            <span className="notecat__n num">{c.n_chargers.toLocaleString('ko-KR')}</span>
            <span
              className="notecat__limit num"
              title="이 분류에 속한 충전기 중 limitYn='Y' 비율"
            >
              {(c.limit_share * 100).toFixed(0)}%
            </span>
          </li>
        ))}
      </ul>
      <p className="notecat__legend">
        오른쪽 숫자는 해당 분류에서 <code>limitYn=&#39;Y&#39;</code> 비율입니다 — 높을수록 그 문구가
        이용 제한을 실제로 설명합니다.
      </p>
    </ChartFrame>
  )
}
